#!/usr/bin/env node

/**
 * spacecode - Cloud dev environments with AI coding agents
 *
 * Launch fully-configured AI coding environments in the cloud in seconds.
 */

import { Command } from 'commander';
import { initPostHog } from './posthog.js';

// Initialize Sentry for error tracking and logging
initPostHog();

import { launch } from './commands/launch.js';
import { status } from './commands/status.js';
import { list } from './commands/list.js';
import { stop } from './commands/stop.js';
import { destroy } from './commands/destroy.js';
import { clean } from './commands/clean.js';
import { exec } from './commands/exec.js';
import { run } from './commands/run.js';
import { execBatch } from './commands/exec-batch.js';
import { tasks, cancelTask } from './commands/tasks.js';
import { syncCommand } from './commands/sync.js';
import { attach } from './commands/attach.js';
import { interactiveHub, resolveTarget } from './interactive.js';
import { login, logout, whoami } from './commands/auth.js';
import { registerTokenCommand } from './commands/token.js';
import { registerAgentCommand } from './commands/agent.js';
import { registerSentryCommand } from './commands/sentry.js';
import { applyApiKeyFlag } from './config.js';
import { listConfigs, getConfig, setConfig, deleteConfig, showConfigPaths, setApiUrl, showLocalConfig } from './commands/config.js';
import { analytics } from './commands/analytics.js';

const program = new Command();

program
  .name('spacecode')
  .description('SpaceCode - launch cloud dev environments with AI coding agents')
  .version('2.16.6')
  .enablePositionalOptions();

program
  .command('launch')
  .description('Launch a new cloud session')
  .option('-R, --repo <repo>', 'Clone a GitHub repository into workspace (URL or owner/repo)')
  .option('-n, --new [name]', 'Start a new project (optionally specify name, or will prompt)')
  .option('--name <name>', 'Name this session for easy reconnection')
  .option('--destroy', 'Destroy the session on exit (default: session stays alive)')
  .option('-t, --idle-timeout <minutes>', 'Auto-shutdown timeout in minutes (default: 15)', '15')
  .option('--proxy-mode <mode>', 'API proxy mode: "legacy" (round-robin GLM) or "pipeline" (multi-model router)', 'pipeline')
  .option('--own-key', 'Bring your own API key (no proxy)')
  .option('--api-key <key>', 'Use a specific API key (bypasses proxy)')
  .action(async (options) => {
    await launchWithOptions(options);
  });

program
  .command('attach <id-or-name>')
  .description('Reconnect to a running session (by ID or name)')
  .option('-d, --destroy', 'Destroy the session on disconnect (like launch)')
  .action(async (idOrName, options) => {
    await attach(idOrName, {
      destroy: options.destroy,
    });
  });

program
  .command('status')
  .description('Show current session status')
  .action(async () => {
    await status();
  });

program
  .command('list')
  .description('List all sessions')
  .option('-a, --all', 'Show all sessions including destroyed')
  .action(async (options) => {
    await list({ all: options.all });
  });

program
  .command('stop <id-or-name>')
  .description('Stop a specific session (by ID or name)')
  .action(async (idOrName) => {
    await stop(idOrName);
  });

program
  .command('destroy <id-or-name>')
  .description('Destroy a specific session (by ID or name)')
  .action(async (idOrName) => {
    try {
      await destroy(idOrName);
    } catch (error) {
      const chalk = (await import('chalk')).default;
      console.error(chalk.red(`Error: ${error instanceof Error ? error.message : String(error)}`));
      process.exit(1);
    }
  });

program
  .command('clean')
  .description('Clean up stale task records')
  .option('-f, --force', 'Force mark stale tasks as completed')
  .action(async (options) => {
    await clean({ force: options.force });
  });

program
  .command('exec')
  .description('Dispatch a headless task to a remote sandbox (fire-and-forget)')
  .requiredOption('-R, --repo <repo>', 'GitHub repository (owner/repo or URL)')
  .argument('<prompt>', 'Task prompt to execute')
  .option('-r, --region <region>', '(ignored) Cloud region')
  .option('-s, --size <size>', '(ignored) Sandbox size')
  .option('-m, --memory <mb>', '(ignored) Override memory in MB')
  .option('-b, --branch <branch>', 'Branch name for the task')
  .option('--no-commit', 'Skip auto-commit and PR creation')
  .option('-t, --idle-timeout <minutes>', 'Auto-shutdown timeout in minutes', '30')
  .option('--proxy-mode <mode>', 'API proxy mode: "legacy" or "pipeline"', 'pipeline')
  .action(async (prompt, options) => {
    const idleTimeout = parseInt(options.idleTimeout, 10);
    if (isNaN(idleTimeout) || idleTimeout < 0) {
      const chalk = (await import('chalk')).default;
      console.error(chalk.red('Error: --idle-timeout must be a non-negative integer'));
      process.exit(1);
    }
    if (options.region || options.size || options.memory) {
      const chalk = (await import('chalk')).default;
      console.warn(chalk.yellow('Warning: --region, --size, and --memory flags are ignored (compute is managed by the sandbox worker)'));
    }
    await exec({
      repo: options.repo,
      prompt,
      branch: options.branch,
      noCommit: !options.commit,
      idleTimeout,
      proxyMode: options.proxyMode,
    });
  });

program
  .command('run')
  .description('Run a task on a remote sandbox with streaming output')
  .requiredOption('-R, --repo <repo>', 'GitHub repository (owner/repo or URL)')
  .argument('<prompt>', 'Task prompt to execute')
  .option('-r, --region <region>', '(ignored) Cloud region')
  .option('-s, --size <size>', '(ignored) Sandbox size')
  .option('-m, --memory <mb>', '(ignored) Override memory in MB')
  .option('-b, --branch <branch>', 'Branch name for the task')
  .option('--no-commit', 'Skip auto-commit and PR creation')
  .option('-t, --idle-timeout <minutes>', 'Auto-shutdown timeout in minutes', '30')
  .option('--proxy-mode <mode>', 'API proxy mode: "legacy" or "pipeline"', 'pipeline')
  .action(async (prompt, options) => {
    const idleTimeout = parseInt(options.idleTimeout, 10);
    if (isNaN(idleTimeout) || idleTimeout < 0) {
      const chalk = (await import('chalk')).default;
      console.error(chalk.red('Error: --idle-timeout must be a non-negative integer'));
      process.exit(1);
    }
    if (options.region || options.size || options.memory) {
      const chalk = (await import('chalk')).default;
      console.warn(chalk.yellow('Warning: --region, --size, and --memory flags are ignored (compute is managed by the sandbox worker)'));
    }
    await run({
      repo: options.repo,
      prompt,
      branch: options.branch,
      noCommit: !options.commit,
      idleTimeout,
      proxyMode: options.proxyMode,
    });
  });

program
  .command('tasks')
  .description('List and manage remote tasks')
  .option('-a, --all', 'Show all tasks (including completed)')
  .option('--prune', 'Remove old completed tasks')
  .option('--clean-stale', 'Mark stale running tasks as completed (checks sandbox state)')
  .option('--force-clean', 'Mark as stale even if sandbox is still running (use with --clean-stale)')
  .option('--stale-age <hours>', 'Hours of inactivity to consider stale (default: 12)', parseInt)
  .option('--status <status>', 'Filter by status (pending, running, completed, failed, cancelled)')
  .option('--limit <n>', 'Show only the most recent N tasks', parseInt)
  .option('--repo <repo>', 'Filter by repository name')
  .action(async (options) => {
    await tasks({
      all: options.all,
      prune: options.prune,
      cleanStale: options.cleanStale,
      forceClean: options.forceClean,
      staleAge: options.staleAge as number | undefined,
      status: options.status as string | undefined,
      limit: options.limit as number | undefined,
      repo: options.repo as string | undefined,
    });
  });

program
  .command('cancel <task-id>')
  .description('Cancel a running task and destroy its sandbox')
  .action(async (taskId) => {
    try {
      await cancelTask(taskId);
    } catch (error) {
      const chalk = (await import('chalk')).default;
      console.error(chalk.red(`Error: ${error instanceof Error ? error.message : String(error)}`));
      process.exit(1);
    }
  });

program
  .command('exec-batch')
  .description('Dispatch multiple tasks in parallel to remote sandboxes')
  .argument('<json>', 'JSON array of task specs: [{repo, prompt, branch?}, ...]')
  .action(async (json) => {
    const chalk = (await import('chalk')).default;
    let specs;
    try {
      specs = JSON.parse(json);
      if (!Array.isArray(specs)) throw new Error('Input must be a JSON array');
    } catch (e) {
      console.error(chalk.red(`Error: Invalid JSON - ${e instanceof Error ? e.message : String(e)}`));
      process.exit(1);
    }
    try {
      const result = await execBatch(specs);
      console.log(chalk.bold(`\nBatch ${result.groupId}: ${result.dispatched}/${result.total} dispatched`));
      for (const task of result.tasks) {
        const icon = task.status === 'dispatched' ? chalk.green('OK') : chalk.red('FAIL');
        console.log(`  [${icon}] ${task.taskId}  ${task.repo}  ${task.branch}`);
        if (task.error) console.log(chalk.red(`       ${task.error}`));
      }
    } catch (error) {
      console.error(chalk.red(`Error: ${error instanceof Error ? error.message : String(error)}`));
      process.exit(1);
    }
  });

program
  .command('sync')
  .description('Sync project status to GitHub Projects and Issues')
  .argument('[repo]', 'Repository to sync (owner/repo, default: auto-detect)')
  .option('--full', 'Sync all data (project + issues)')
  .option('--issues-only', 'Sync issues only (TODOs, test failures)')
  .option('--project-only', 'Sync project board only')
  .option('--dry-run', 'Show what would change without modifying')
  .option('-v, --verbose', 'Show detailed output')
  .action(async (repo, options) => {
    await syncCommand(repo, options);
  });

program
  .command('login')
  .description('Sign in with a SpaceCode API key')
  .option('--api-key <key>', 'SpaceCode API key (sk_...) from your dashboard')
  .action(async (opts: { apiKey?: string }) => {
    await login({ apiKey: opts.apiKey });
  });

const IMPORT_AGENTS = ['claude'] as const;

type ImportOpts = { dryRun?: boolean; overwrite?: boolean; adapt?: boolean; yes?: boolean; restoreOriginal?: boolean };

async function runImport(agent: string, opts: ImportOpts): Promise<void> {
  if (!(IMPORT_AGENTS as readonly string[]).includes(agent)) {
    console.error(`Unknown agent "${agent}". Supported: ${IMPORT_AGENTS.join(', ')}`);
    process.exit(2);
  }
  const { startLocalRepl } = await import('./commands/repl.js');
  const code = await startLocalRepl({
    args: ['import', agent, ...(opts.dryRun ? ['--dry-run'] : []), ...(opts.overwrite ? ['--overwrite'] : []), ...(opts.adapt === false ? ['--no-adapt'] : []), ...(opts.yes ? ['--yes'] : []), ...(opts.restoreOriginal ? ['--restore-original'] : [])],
  });
  process.exit(code);
}

const withImportOptions = (cmd: ReturnType<typeof program.command>) =>
  cmd
    .option('--dry-run', 'Show what would be imported without writing anything')
    .option('--overwrite', 'Replace profile items that differ from the agent\'s files')
    .option('--no-adapt', 'Import instructions as is instead of adapting them (default: a short always-on core plus on-demand sections, confirmed before storing)')
    .option('--yes', 'Store the adapted result without asking')
    .option('--restore-original', 'Put the unadapted instructions back as your loaded instructions');

withImportOptions(
  program
    .command('import <agent>')
    .description(`Import another coding agent's instructions, memory, commands, skills and MCP servers into your SpaceCode profile (agents: ${IMPORT_AGENTS.join(', ')})`),
).action(async (agent: string, opts: ImportOpts) => runImport(agent, opts));

// Deprecated spelling of `import claude`, kept so existing scripts keep working.
withImportOptions(program.command('import-claude', { hidden: true }).description('Deprecated: use `spacecode import claude`')).action(async (opts: ImportOpts) => runImport('claude', opts));

program
  .command('approve-mcp')
  .description('Approve the stdio commands of your imported MCP servers on this machine (each one separately; a changed command asks again)')
  .action(async () => {
    const { startLocalRepl } = await import('./commands/repl.js');
    const code = await startLocalRepl({ args: ['approve-mcp'] });
    process.exit(code);
  });

program
  .command('logout')
  .description('Clear authentication credentials')
  .action(async () => {
    await logout();
  });

program
  .command('whoami')
  .description('Show current user information')
  .action(async () => {
    await whoami();
  });

program
  .command('analytics')
  .description('Show API proxy pipeline analytics dashboard')
  .option('--secret <secret>', 'Debug secret for API proxy (or set SPACECODE_DEBUG_SECRET)')
  .option('--json', 'Output raw JSON instead of formatted dashboard')
  .option('--url <url>', 'API proxy URL (default: production)')
  .action(async (options) => {
    await analytics({
      secret: options.secret,
      json: options.json,
      url: options.url,
    });
  });

registerTokenCommand(program);
registerAgentCommand(program);
registerSentryCommand(program);

program
  .command('sessions')
  .description('List recent agent sessions (alias for "agent sessions")')
  .option('-a, --all', 'Show all sessions')
  .option('--limit <n>', 'Number of sessions to show', '20')
  .option('--work-items', 'Show open work items and decisions instead of sessions')
  .option('--repo <repo>', 'Filter work items by repository (with --work-items)')
  .option('--status <status>', 'Filter work items by status: open, done, all (with --work-items)', 'open')
  .action(async (options) => {
    const { getTtyUrl, getAuthToken, listSessions, listWorkItems, parseWorkItemTimestamp } = await import('./commands/agent.js');
    const chalk = (await import('chalk')).default;

    if (options.workItems) {
      if (!['open', 'done', 'all'].includes(options.status)) {
        console.error(chalk.red(`Invalid --status "${options.status}": must be open, done, or all`));
        process.exit(1);
      }
      try {
        const [ttyUrl, token] = await Promise.all([getTtyUrl(), getAuthToken()]);
        const limit = options.all ? 200 : parseInt(options.limit, 10);
        const items = await listWorkItems(ttyUrl, token, {
          repo: options.repo,
          status: options.status,
          limit,
        });

        if (items.length === 0) {
          console.log(chalk.gray('No work items found.'));
          return;
        }

        console.log(chalk.bold(`\n  Work Items (${items.length}):\n`));

        for (const item of items) {
          const age = formatSessionAge(parseWorkItemTimestamp(item.updated_at));
          const kindLabel = item.kind === 'decision' ? chalk.magenta('[decision]') : chalk.cyan('[work_item]');
          const statusColor = item.status === 'open' ? chalk.yellow : chalk.gray;
          console.log(`  ${kindLabel} ${statusColor(item.status)}  ${chalk.dim(item.repo_id)}  ${chalk.dim(age)}`);
          console.log(`    ${item.description}`);
          console.log(`    ${chalk.dim(`session: ${item.session_name ?? item.session_id.slice(0, 8)}`)}`);
        }

        console.log('');
      } catch (error) {
        console.error(chalk.red(`Failed: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
      return;
    }

    try {
      const [ttyUrl, token] = await Promise.all([getTtyUrl(), getAuthToken()]);
      const limit = options.all ? 100 : parseInt(options.limit, 10);
      const sessions = await listSessions(ttyUrl, token, limit);

      if (sessions.length === 0) {
        console.log(chalk.gray('No sessions found. Start one with: spacecode owner/repo'));
        return;
      }

      console.log(chalk.bold(`\n  Agent Sessions (${sessions.length}):\n`));

      for (const s of sessions) {
        const age = formatSessionAge(s.updated_at);
        const statusColor = s.status === 'active' ? chalk.green : s.status === 'idle' ? chalk.yellow : chalk.gray;
        const label = s.name ? chalk.cyan(s.name) : chalk.gray(s.id.slice(0, 8));
        const repo = s.repo_id ? chalk.dim(` ${s.repo_id}`) : '';
        const msgCount = s.message_count ? chalk.dim(` ${s.message_count} msgs`) : '';
        console.log(`  ${label}  ${statusColor(s.status)}${repo}${msgCount}  ${chalk.dim(age)}`);
        if (s.name) console.log(`    ${chalk.dim(`ID: ${s.id}`)}`);
        if (s.last_prompt) {
          let preview = '';
          try {
            const parsed = JSON.parse(s.last_prompt);
            preview = typeof parsed === 'string' ? parsed : String(parsed);
          } catch {
            preview = String(s.last_prompt);
          }
          if (preview.length > 60) preview = preview.slice(0, 57) + '...';
          console.log(`    ${chalk.dim(`> ${preview}`)}`);
        }
      }

      console.log('');
      console.log(chalk.dim('  Resume with: spacecode --session <id>'));
      console.log('');
    } catch (error) {
      console.error(chalk.red(`Failed: ${error instanceof Error ? error.message : String(error)}`));
      process.exit(1);
    }
  });

function formatSessionAge(timestampMs: number): string {
  const diff = Date.now() - timestampMs;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

program
  .command('config')
  .description('Manage local config and backend configs')
  .option('-l, --list', 'List all backend configs')
  .option('--local', 'Show local configuration')
  .option('--api-url <url>', 'Set backend API URL in local config')
  .option('-g, --get <key>', 'Get a specific backend config (claudeMd, settingsJson, settingsLocal, prd, progress)')
  .option('-s, --set <key>', 'Set a backend config from file (use: --set <key> < <file>)')
  .option('-d, --delete <key>', 'Delete a backend config')
  .option('-h, --help', 'Show config command help')
  .action(async (options) => {
    if (options.apiUrl) {
      await setApiUrl(options.apiUrl);
      return;
    }
    if (options.local) {
      await showLocalConfig();
      return;
    }
    if (options.help) {
      showConfigPaths();
      return;
    }
    if (options.list) {
      await listConfigs();
    } else if (options.get) {
      await getConfig(options.get);
    } else if (options.set) {
      await setConfig(options.set, '-');  // Read from stdin
    } else if (options.delete) {
      await deleteConfig(options.delete);
    } else {
      showConfigPaths();
    }
  });

// Helper to parse launch options and call launch()
export async function launchWithOptions(options: Record<string, any>): Promise<void> {
  const idleTimeout = parseInt(options.idleTimeout ?? '15', 10);
  if (isNaN(idleTimeout) || idleTimeout < 0) {
    const chalk = (await import('chalk')).default;
    console.error(chalk.red('Error: --idle-timeout must be a non-negative integer'));
    process.exit(1);
  }
  await launch({
    repo: options.repo,
    newProject: options.new !== undefined,
    newProjectName: typeof options.new === 'string' ? options.new : undefined,
    destroy: options.destroy,
    idleTimeout,
    proxyMode: options.proxyMode,
    name: options.name,
    ownKey: options.ownKey,
    apiKey: options.apiKey,
  });
}

// Helper: read all of stdin with a 30-second timeout
async function readStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  let timerId: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timerId = setTimeout(() => reject(new Error('stdin read timed out after 30 seconds')), 30_000);
  });
  const read = (async () => {
    for await (const chunk of process.stdin) {
      chunks.push(chunk);
    }
  })();
  try {
    await Promise.race([read, timeout]);
  } finally {
    clearTimeout(timerId!);
  }
  return Buffer.concat(chunks).toString().trim();
}

// Default: smart routing between launch and attach
program
  .argument('[target]', 'Repo (owner/repo) to launch, or session name/ID to attach')
  .option('-p, --prompt [prompt]', 'Run headless (non-interactive)')
  .option('--output-format <format>', 'Output format for -p: text (default) or json', 'text')
  .option('-v, --verbose', 'Show detailed progress (phases, stderr)')
  .option('-R, --repo <repo>', 'Clone a GitHub repository into workspace')
  .option('-n, --new [name]', 'Start a new project')
  .option('--name <name>', 'Name this session for easy reconnection')
  .option('--destroy', 'Destroy the session on exit')
  .option('-b, --branch <branch>', 'Branch name for the task')
  .option('--no-commit', 'Skip auto-commit and PR creation')
  .option('-t, --idle-timeout <minutes>', 'Auto-shutdown timeout (default: 15)', '15')
  .option('--proxy-mode <mode>', 'API proxy mode', 'pipeline')
  .option('--own-key', 'Bring your own API key')
  .option('--api-key <key>', 'Use a specific API key')
  .option('--session <id>', 'Resume an existing agent session')
  .option('--cloud', 'With no target: start a cloud agent session instead of the local REPL')
  .option('--yolo', 'Local REPL: skip confirmation prompts (policy denies still apply)')
  .option('--continue', 'Local REPL: resume the newest saved conversation for this project')
  .action(async (target, options) => {
    try {
      applyApiKeyFlag(options.apiKey);
      // No subcommand, target or mode flag → local agent REPL (packages/agent-ui)
      if (!target && !options.session && options.prompt === undefined && !options.cloud && !options.repo && !options.new) {
        const { startLocalRepl } = await import('./commands/repl.js');
        process.exit(await startLocalRepl({ yolo: options.yolo, resume: options.continue }));
      }

      // Session resume mode
      if (options.session) {
        let sessionId = options.session;
        // If it doesn't look like a UUID, resolve by name
        if (!/^[0-9a-f]{8}-/.test(sessionId)) {
          const { getTtyUrl, getAuthToken, lookupSessionByName } = await import('./commands/agent.js');
          const [ttyUrl, token] = await Promise.all([getTtyUrl(), getAuthToken()]);
          const resolved = await lookupSessionByName(ttyUrl, token, sessionId);
          if (!resolved) {
            process.stderr.write(`error: no session named "${sessionId}" found\n`);
            process.exit(1);
          }
          sessionId = resolved;
        }
        const { startInteractiveSession } = await import('./agent/interactive.js');
        return await startInteractiveSession({ sessionId, name: options.name });
      }

      // Headless mode: -p flag
      if (options.prompt !== undefined) {
        const { headless } = await import('./commands/headless.js');
        const repo = options.repo ?? target;
        if (!repo) {
          process.stderr.write('error: --repo or target repo required with -p\n');
          process.exit(1);
        }

        // Validate --output-format
        const outputFormat = options.outputFormat;
        if (outputFormat !== 'text' && outputFormat !== 'json') {
          process.stderr.write(`error: --output-format must be "text" or "json", got "${outputFormat}"\n`);
          process.exit(1);
        }

        let prompt: string;
        if (typeof options.prompt === 'string') {
          prompt = options.prompt;
        } else if (!process.stdin.isTTY) {
          try {
            prompt = await readStdin();
          } catch (readErr) {
            process.stderr.write(`error: failed to read stdin: ${readErr instanceof Error ? readErr.message : String(readErr)}\n`);
            process.exit(1);
          }
        } else {
          process.stderr.write('error: -p requires a prompt argument or stdin\n');
          process.exit(1);
        }

        if (!prompt) {
          process.stderr.write('error: prompt is empty\n');
          process.exit(1);
        }

        const idleTimeout = parseInt(options.idleTimeout, 10);
        if (isNaN(idleTimeout) || idleTimeout < 0) {
          process.stderr.write('error: --idle-timeout must be a non-negative integer\n');
          process.exit(1);
        }

        return headless({
          repo,
          prompt,
          branch: options.branch,
          noCommit: !options.commit,
          idleTimeout,
          proxyMode: options.proxyMode as 'legacy' | 'pipeline',
          outputFormat,
          verbose: options.verbose ?? false,
        });
      }

      // No target → drop into REPL (with auto-resume for recent sessions)
      if (!target) {
        const { startInteractiveSession } = await import('./agent/interactive.js');
        const readline = await import('readline');
        const chalk = (await import('chalk')).default;

        // Auto-resume: check for recent idle session < 5 minutes old
        try {
          const { getTtyUrl, getAuthToken, listSessions } = await import('./commands/agent.js');
          const [ttyUrl, token] = await Promise.all([getTtyUrl(), getAuthToken()]);
          const sessions = await listSessions(ttyUrl, token, 1);
          if (sessions.length > 0) {
            const recent = sessions[0];
            const age = Date.now() - recent.updated_at;
            if (age < 5 * 60 * 1000 && recent.status === 'idle') {
              const rl = readline.createInterface({ input: process.stdin, output: process.stderr });
              const answer = await new Promise<string>((resolve) => {
                rl.question(
                  `  Resume "${chalk.bold(recent.name ?? recent.id.slice(0, 8))}"? ${chalk.dim('(Y/n)')} `,
                  resolve,
                );
              });
              rl.close();
              if (!answer || answer.toLowerCase() === 'y') {
                return await startInteractiveSession({
                  sessionId: recent.id,
                  name: recent.name ?? undefined,
                  repo: recent.repo_id ?? undefined,
                });
              }
            }
          }
        } catch {
          // Ignore — proceed to new session
        }

        return await startInteractiveSession({ name: options.name });
      }
      // Target contains "/" or starts with "http" → treat as repo (interactive agent session)
      if (target.includes('/') || target.startsWith('http')) {
        const { startInteractiveSession } = await import('./agent/interactive.js');
        return await startInteractiveSession({ repo: target, name: options.name });
      }
      // Bare name → search sessions + repos, let user choose
      await resolveTarget(target, options);
    } catch (err) {
      const chalk = (await import('chalk')).default;
      console.error(chalk.red(`Error: ${err instanceof Error ? err.message : String(err)}`));
      process.exit(1);
    }
  });

program.parse();
